import { useId, PropsWithChildren } from 'react';

import InputError from './InputError';

import { Label } from '@/components';
import { PropsWithClassName } from '@/types';
import { cn } from '@/utils';

interface RadioInputProps {
  isChecked: boolean;
  name: string;
  label: string;
  className?: string;
  value: string | number;
  disabled?: boolean;
  onChange: (name: string, value: string | number) => void;
  onBlur?: () => void;
}

type WrapperProps = PropsWithClassName<
  PropsWithChildren<{
    label?: string;
    error?: string;
  }>
>;

const RadioInputWrapper = ({
  children,
  className,
  label,
  error,
}: WrapperProps) => {
  return (
    <fieldset className="space-y-1">
      <div
        aria-label={label}
        aria-required="true"
        className={label ? 'flex flex-col space-y-[10px]' : className}
        dir="ltr"
        role="radiogroup"
      >
        {label && <Label>{label}</Label>}
        {label ? <div className={className}>{children}</div> : children}
      </div>
      {error && <InputError.Label>{error}</InputError.Label>}
    </fieldset>
  );
};

const RadioInputComponent = ({
  isChecked,
  name,
  label,
  className,
  value,
  disabled,
  onChange,
  onBlur,
}: RadioInputProps) => {
  const id = useId();

  const nameId = `${name}-${id}`;

  return (
    <div className="flex items-center space-x-3">
      <button
        aria-checked={isChecked ? 'true' : 'false'}
        className={cn(
          'aspect-square h-4 w-4 rounded-full border flex items-center justify-center focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-offset-1 focus-visible:ring-gdt-blue',
          {
            'bg-gdt-blue-xl2 border-gdt-blue': isChecked,
            'border-gdt-gray-4 bg-white': !isChecked,
            'opacity-50 cursor-not-allowed': disabled,
          }
        )}
        id={nameId}
        role="radio"
        tabIndex={0}
        type="button"
        value={value}
        onBlur={onBlur}
        onClick={() => !disabled && onChange(name, value)}
      >
        {isChecked && (
          <div className="aspect-square rounded-full bg-gdt-blue w-2 h-2" />
        )}
      </button>
      <label
        className={cn(
          'text-sm font-normal text-gdt-gray relative cursor-pointer select-none flex items-center gap-1',
          className,
          {
            'opacity-50 cursor-not-allowed': disabled,
          }
        )}
        htmlFor={nameId}
      >
        <span>{label}</span>
      </label>
    </div>
  );
};

const RadioInput = {
  Wrapper: RadioInputWrapper,
  Input: RadioInputComponent,
};

export default RadioInput;
